import { z } from 'zod';
import { ELEMENT_ANIMATIONS, TRANSITIONS, WIDGET_TYPES } from './constants';

export const transitionSchema = z.enum(TRANSITIONS);
export const animationSchema = z.enum(ELEMENT_ANIMATIONS);

// ───────── Slides (presentation editor ⇄ slides-engine ⇄ player) ─────────

const baseElement = z.object({
  id: z.string(),
  x: z.number(),
  y: z.number(),
  width: z.number().nonnegative(),
  height: z.number().nonnegative(),
  rotation: z.number().default(0),
  opacity: z.number().min(0).max(1).default(1),
  locked: z.boolean().default(false),
  hidden: z.boolean().default(false),
  name: z.string().optional(),
  groupId: z.string().optional(),
  animation: z
    .object({
      type: animationSchema.default('none'),
      delayMs: z.number().min(0).default(0),
      durationMs: z.number().min(0).default(600),
    })
    .optional(),
});

export const shadowSchema = z.object({
  color: z.string().default('rgba(0,0,0,0.35)'),
  blur: z.number().default(8),
  offsetX: z.number().default(2),
  offsetY: z.number().default(2),
});

export const textElementSchema = baseElement.extend({
  type: z.literal('text'),
  text: z.string(),
  fontFamily: z.string().default('Inter'),
  fontSize: z.number().positive().default(48),
  fontWeight: z.union([z.number(), z.string()]).default(400),
  fontStyle: z.enum(['normal', 'italic']).default('normal'),
  textDecoration: z.enum(['none', 'underline', 'line-through']).default('none'),
  color: z.string().default('#0F172A'),
  align: z.enum(['left', 'center', 'right']).default('left'),
  verticalAlign: z.enum(['top', 'middle', 'bottom']).default('top'),
  lineHeight: z.number().default(1.2),
  letterSpacing: z.number().default(0),
  shadow: shadowSchema.optional(),
  stroke: z.object({ color: z.string(), width: z.number() }).optional(),
  background: z.string().optional(),
  padding: z.number().default(0),
});

export const shapeElementSchema = baseElement.extend({
  type: z.literal('shape'),
  shape: z.enum(['rect', 'ellipse', 'triangle', 'star', 'line', 'arrow']),
  fill: z.string().default('#4F46E5'),
  stroke: z.string().optional(),
  strokeWidth: z.number().default(0),
  cornerRadius: z.number().default(0),
  shadow: shadowSchema.optional(),
});

export const imageElementSchema = baseElement.extend({
  type: z.literal('image'),
  /** media asset id or absolute URL */
  src: z.string(),
  mediaId: z.string().optional(),
  fit: z.enum(['cover', 'contain', 'fill']).default('cover'),
  cornerRadius: z.number().default(0),
  animated: z.boolean().default(false),
});

export const videoElementSchema = baseElement.extend({
  type: z.literal('video'),
  src: z.string(),
  mediaId: z.string().optional(),
  fit: z.enum(['cover', 'contain', 'fill']).default('cover'),
  muted: z.boolean().default(true),
  loop: z.boolean().default(true),
});

export const iconElementSchema = baseElement.extend({
  type: z.literal('icon'),
  icon: z.string(),
  color: z.string().default('#0F172A'),
  strokeWidth: z.number().default(2),
});

export const qrElementSchema = baseElement.extend({
  type: z.literal('qr'),
  value: z.string(),
  color: z.string().default('#0F172A'),
  background: z.string().default('#FFFFFF'),
});

export const tableElementSchema = baseElement.extend({
  type: z.literal('table'),
  rows: z.array(z.array(z.string())),
  headerRow: z.boolean().default(true),
  fontFamily: z.string().default('Inter'),
  fontSize: z.number().default(28),
  color: z.string().default('#0F172A'),
  headerBackground: z.string().default('#4F46E5'),
  headerColor: z.string().default('#FFFFFF'),
  rowBackground: z.string().default('#FFFFFF'),
  altRowBackground: z.string().default('#F1F5F9'),
  borderColor: z.string().default('#E2E8F0'),
  columnWidths: z.array(z.number()).optional(),
});

export const svgElementSchema = baseElement.extend({
  type: z.literal('svg'),
  /** Inline SVG markup (sanitized on save) */
  svg: z.string(),
});

export const slideElementSchema = z.discriminatedUnion('type', [
  textElementSchema,
  shapeElementSchema,
  imageElementSchema,
  videoElementSchema,
  iconElementSchema,
  qrElementSchema,
  tableElementSchema,
  svgElementSchema,
]);
export type SlideElement = z.infer<typeof slideElementSchema>;
export type TextElement = z.infer<typeof textElementSchema>;
export type ShapeElement = z.infer<typeof shapeElementSchema>;
export type ImageElement = z.infer<typeof imageElementSchema>;
export type VideoElement = z.infer<typeof videoElementSchema>;
export type TableElement = z.infer<typeof tableElementSchema>;

export const backgroundSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('color'), color: z.string() }),
  z.object({
    type: z.literal('gradient'),
    from: z.string(),
    to: z.string(),
    angle: z.number().default(135),
  }),
  z.object({
    type: z.literal('image'),
    src: z.string(),
    mediaId: z.string().optional(),
    fit: z.enum(['cover', 'contain']).default('cover'),
  }),
  z.object({ type: z.literal('video'), src: z.string(), mediaId: z.string().optional() }),
]);
export type SlideBackground = z.infer<typeof backgroundSchema>;

export const slideSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  durationMs: z.number().min(1000).default(10000),
  transition: transitionSchema.default('fade'),
  transitionMs: z.number().min(0).default(800),
  hidden: z.boolean().default(false),
  background: backgroundSchema.default({ type: 'color', color: '#FFFFFF' }),
  elements: z.array(slideElementSchema).default([]),
  notes: z.string().optional(),
});
export type Slide = z.infer<typeof slideSchema>;

export const themeSchema = z.object({
  name: z.string().optional(),
  primary: z.string(),
  secondary: z.string(),
  background: z.string(),
  text: z.string(),
  fontHeading: z.string(),
  fontBody: z.string(),
  logoUrl: z.string().optional(),
});
export type Theme = z.infer<typeof themeSchema>;

export const presentationDocSchema = z.object({
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  slides: z.array(slideSchema),
  theme: themeSchema.optional(),
});
export type PresentationDoc = z.infer<typeof presentationDocSchema>;

// ───────── Playlists ─────────

export const daypartSchema = z.object({
  days: z.array(z.number().int().min(0).max(6)).optional(),
  startTime: z.string().regex(/^\d{1,2}:\d{2}$/),
  endTime: z.string().regex(/^\d{1,2}:\d{2}$/),
});

export const playlistItemSchema = z.object({
  id: z.string(),
  kind: z.enum(['media', 'presentation', 'widget', 'playlist']),
  refId: z.string(),
  durationMs: z.number().int().min(1000).default(10000),
  transition: transitionSchema.default('fade'),
  validFrom: z.string().nullish(),
  validUntil: z.string().nullish(),
  dayparts: z.array(daypartSchema).optional(),
});
export type PlaylistItem = z.infer<typeof playlistItemSchema>;

// ───────── Layouts ─────────

export const zoneSchema = z.object({
  id: z.string(),
  name: z.string().default('Zone'),
  x: z.number(),
  y: z.number(),
  w: z.number().positive(),
  h: z.number().positive(),
  z: z.number().int().default(0),
  content: z
    .object({ kind: z.enum(['playlist', 'widget', 'media', 'presentation']), refId: z.string() })
    .nullable()
    .default(null),
  background: z.string().optional(),
});
export type Zone = z.infer<typeof zoneSchema>;

// ───────── Widgets ─────────

export const widgetConfigSchema = z.record(z.string(), z.unknown());
export const widgetTypeSchema = z.enum(WIDGET_TYPES);

// ───────── Schedules ─────────

export const scheduleRuleSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  content: z.object({
    kind: z.enum(['playlist', 'layout', 'presentation', 'media']),
    id: z.string(),
  }),
  days: z.array(z.number().int().min(0).max(6)).optional(),
  startTime: z.string().nullish(),
  endTime: z.string().nullish(),
  startDate: z.string().nullish(),
  endDate: z.string().nullish(),
  priority: z.number().int().default(0),
  enabled: z.boolean().default(true),
});

export const emergencyMessageSchema = z.object({
  active: z.boolean(),
  title: z.string().max(120),
  body: z.string().max(1000),
  background: z.string().default('#B91C1C'),
  color: z.string().default('#FFFFFF'),
  screenIds: z.array(z.string()).nullable().default(null),
  startedAt: z.string().optional(),
});
export type EmergencyMessage = z.infer<typeof emergencyMessageSchema>;

// ───────── Player telemetry ─────────

export const heartbeatSchema = z.object({
  playerVersion: z.string().max(40),
  platform: z.string().max(40),
  resolution: z.string().max(20).optional(),
  freeStorageBytes: z.number().optional(),
  cpuPercent: z.number().optional(),
  temperatureC: z.number().optional(),
  memoryMb: z.number().optional(),
  currentContent: z.string().max(200).optional(),
  manifestVersion: z.string().max(80).optional(),
  error: z.string().max(500).optional(),
});
export type Heartbeat = z.infer<typeof heartbeatSchema>;

export const proofOfPlayBatchSchema = z.object({
  events: z
    .array(
      z.object({
        contentKind: z.string().max(20),
        contentId: z.string().max(40),
        contentName: z.string().max(200).optional(),
        startedAt: z.string(),
        durationMs: z.number().int().min(0).max(86_400_000),
      }),
    )
    .max(1000),
});
